import React, { useState, useEffect, useContext } from "react";
import {
  Col,
  Row,
  Button,
  Typography,
  Tag,
  Badge,
  Rate,
  Image,
  Popover,
  message,
  Modal,
} from "antd";
import { EditOutlined, EyeOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

import "./index.css";
import { getApi, preloadImage, saveApi } from "../../../../helpers";
import Sidebar from "../../../../components/Sidebar";
import UploadImage from "../../../../components/UploadImage";
import Popup from "../../../../components/Popup";
import { StoreContext } from "../../../../context";
import { canWrite } from "../../../../helpers/permissions";

const { Title, Text } = Typography;

export default function ProductSidebar({
  visible,
  product_id,
  editProductHandler,
  onClose,
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState({});
  const [variant, setVariant] = useState({});
  const [popover, setPopover] = useState(false);
  const [display, setDisplay] = useState("");
  const [toDeleteImage, setToDeleteImage] = useState("");
  
  // Add new states for image preview modal
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewImages, setPreviewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (product_id) getProductById();
  }, []);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!previewVisible) return;
      
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevImage();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNextImage();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setPreviewVisible(false);
      }
    };

    // Add event listener when modal is visible
    if (previewVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewVisible, currentImageIndex, previewImages.length]);

  async function getProductById() {
    setLoading(true);
    let vars = [];
    let payload = {};
    storeId
      ? (payload = { product_id: product_id, store_id: storeId })
      : (payload = { product_id: product_id });
    const res = await getApi(payload, "getProductById");
    if (res.data && res.data.variants && res.data.variants.length > 0) {
      vars = res.data.variants.map((variant, index) => {
        let imgArr = [];
        if (variant.images && variant.images.length > 0) {
          if (index === 0) {
            setDisplay(variant.images[0].url);
          }
          imgArr = variant.images.map((image) => {
            preloadImage(image.url);
            return (image = {
              ...image,
              uid: "-1" + image.image_id,
              status: "done",
              name: "image.png",
            });
          });
        }
        return (variant = { ...variant, images: imgArr });
      });
      setDetails({ ...res.data, vars: vars });
      setVariant(vars[0]);
    }
    setLoading(false);
  }

  const deleteImageHandler = async () => {
    setLoading(true);
    const res = await saveApi(
      { product_image_id: toDeleteImage },
      "Product Image Deleted",
      "deleteProductImage"
    );
    if (res) {
      setDisplay("");
      setToDeleteImage("");
      getProductById();
    } else {
      setLoading(false);
    }
  };

  // Add image preview handler
  const handleImagePreview = (image) => {
    const images = variant.images || [];
    const currentIndex = images.findIndex(img => img.url === image.url);
    
    setPreviewImages(images);
    setPreviewImage(image.url);
    setCurrentImageIndex(currentIndex);
    setPreviewVisible(true);
  };

  // Navigation functions
  const handlePrevImage = () => {
    if (previewImages.length <= 1) return;
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : previewImages.length - 1;
    setCurrentImageIndex(newIndex);
    setPreviewImage(previewImages[newIndex].url);
  };

  const handleNextImage = () => {
    if (previewImages.length <= 1) return;
    const newIndex = currentImageIndex < previewImages.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(newIndex);
    setPreviewImage(previewImages[newIndex].url);
  };

  return (
    <>
      {/* Image Preview Modal */}
      <Modal
        visible={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="90vw"
        style={{ maxWidth: '800px' }}
        centered
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ position: 'relative', textAlign: 'center' }}>
          <Image
            src={previewImage}
            style={{ 
              width: '100%', 
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
            preview={false}
          />
          
          {/* Navigation arrows - only show if there are multiple images */}
          {previewImages.length > 1 && (
            <>
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={handlePrevImage}
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
              
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={handleNextImage}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </>
          )}
          
          {/* Image counter */}
          {previewImages.length > 1 && (
            <div style={{
              position: 'absolute',
              bottom: '16px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '14px'
            }}>
              {currentImageIndex + 1} / {previewImages.length}
            </div>
          )}
        </div>
      </Modal>

      {toDeleteImage && !loading && (
        <Popup
          saveText="Yes, Delete"
          title="Delete Product Image"
          saveHandler={deleteImageHandler}
          visible={toDeleteImage && !loading ? true : false}
          cancelHandler={() => setToDeleteImage("")}
        >
          <Title style={{ fontSize: 18 }}>
            Are you sure to delete the Image ?
          </Title>
          <Text type="danger">This action cannot be reverted.</Text>
        </Popup>
      )}
      <Sidebar
        title="Product Details"
        visible={visible}
        onClose={onClose}
        loading={loading}
        footer={
          !storeId && canWrite() && (
            <div style={{
              display: 'flex',
              gap: '12px',
              padding: '16px 0'
            }}>
              <Button
                icon={<EditOutlined />}
                onClick={editProductHandler}
                type="primary"
                size="large"
                style={{
                  width: '100%',
                  height: '48px',
                  borderRadius: '8px'
                }}
              >
                Edit Product Details
              </Button>
            </div>
          )
        }
      >
        <div style={{ padding: '0 4px' }}>
          {/* Image Section */}
          <div style={{ marginBottom: '16px', backgroundColor: '#F7F7F7', borderRadius: '12px', padding: '12px' }}>
            {/* Upload component with custom preview handler */}
            {!storeId && (
              <div style={{ marginTop: '16px' }}>
                <UploadImage
                  title="Add Product Variant Image"
                  type="product"
                  apiName="uploadProductImage"
                  onUploadDone={(res) => getProductById()}
                  type_id={details.product_id}
                  variant_id={variant.variant_id}
                  setLoading={setLoading}
                  fileList={
                    variant.images && variant.images.length > 0
                      ? variant.images
                      : []
                  }
                  done={
                    (variant.images && variant.images.length > 3) || storeId
                      ? true
                      : false
                  }
                  onRemove={(image) =>
                    storeId
                      ? message.warn(
                          "You do not have access to delete this image."
                        )
                      : setToDeleteImage(image.image_id)
                  }
                  onPreview={handleImagePreview}
                  style={{ 
                    borderRadius: '12px' 
                  }}
                />
              </div>
            )}
            
            {/* For storeId users, show images in a simple grid with preview */}
            {storeId && variant.images && variant.images.length > 0 && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', 
                gap: '12px',
                marginTop: '16px'
              }}>
                {variant.images.map((image, index) => (
                  <div 
                    key={index}
                    style={{
                      position: 'relative',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px solid #f0f0f0'
                    }}
                    onClick={() => handleImagePreview(image)}
                  >
                    <Image 
                      width="100%" 
                      height={120} 
                      src={image.url} 
                      style={{ objectFit: 'cover' }}
                      preview={false}
                    />
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0,0,0,0)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = 1;
                      e.target.style.backgroundColor = 'rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = 0;
                      e.target.style.backgroundColor = 'rgba(0,0,0,0)';
                    }}
                    >
                      <EyeOutlined style={{ color: 'white', fontSize: '24px' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Title and Status */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: "flex", justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <Title style={{ 
                margin: 0, 
                fontSize: '24px',
                fontWeight: 600,
                lineHeight: 1.2,
                flex: 1
              }} level={3}>
                {details.name}
              </Title>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: "4px 12px",
                  textTransform: "uppercase",
                  borderRadius: '6px',
                  marginLeft: '12px',
                  backgroundColor: (storeId && variant.store_is_active === 0) ||
                    variant.variant_is_active === 0 
                    ? '#fff2f0' 
                    : '#f6ffed',
                  color: (storeId && variant.store_is_active === 0) ||
                    variant.variant_is_active === 0 
                    ? '#ff4d4f' 
                    : '#52c41a',
                  border: `1px solid ${(storeId && variant.store_is_active === 0) ||
                    variant.variant_is_active === 0 
                    ? '#ffccc7' 
                    : '#b7eb8f'}`
                }}
              >
                {(storeId && variant.store_is_active === 0) ||
                variant.variant_is_active === 0
                  ? "Inactive"
                  : "Active"}
              </span>
              {storeId && (
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    padding: '6px 12px',
                    borderRadius: '6px',
                    textTransform: "capitalize",
                    marginLeft: '12px',
                    backgroundColor: variant.variant_status === "finishing soon"
                      ? '#fff7e6'
                      : variant.variant_status === "out of stock"
                      ? '#fff2f0'
                      : '#f6ffed',
                    color: variant.variant_status === "finishing soon"
                      ? '#fa8c16'
                      : variant.variant_status === "out of stock"
                      ? '#ff4d4f'
                      : '#52c41a',
                    border: `1px solid ${variant.variant_status === "finishing soon"
                      ? '#ffd591'
                      : variant.variant_status === "out of stock"
                      ? '#ffccc7'
                      : '#b7eb8f'}`
                  }}
                >
                  {variant.variant_status}
                </span>
              )}
            </div>
            
            <Text type="secondary" style={{ fontSize: '14px' }}>
              SKU: {variant.variant_sku}
            </Text>
          </div>

          {/* Rating */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Rate
                allowHalf
                disabled
                style={{ color: "#faad14", fontSize: '16px' }}
                value={parseFloat(details.avg_rate)}
              />
              <Text style={{ color: '#666', fontSize: '14px' }}>
                {details.avg_rate} ({details.reviews ? details.reviews.length : 0} reviews)
              </Text>
            </div>
          </div>

          {/* Price Information */}
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontSize: '16px', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
              Price Information
            </Text>
            <div style={{ 
              backgroundColor: '#fafafa', 
              padding: '12px', 
              borderRadius: '8px',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Text style={{ fontSize: '14px', color: '#666' }}>Cost Price:</Text>
                <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                  ₹ {variant.cost_price === "0.00" ? "0" : variant.cost_price}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Text style={{ fontSize: '14px', color: '#666' }}>MRP:</Text>
                <Text style={{ fontSize: '14px', fontWeight: 500 }}>
                  ₹ {variant.mrp === "0.00" ? "0" : variant.mrp}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: '14px', color: '#666' }}>Sell Price:</Text>
                <Text style={{ fontSize: '14px', fontWeight: 500, color: '#52c41a' }}>
                  ₹ {variant.price === "0.00" ? "0" : variant.price}
                </Text>
              </div>
              {variant.mrp !== "0.00" && variant.price !== "0.00" && parseFloat(variant.mrp) > parseFloat(variant.price) && (
                <div style={{ 
                  marginTop: '8px', 
                  padding: '4px 8px', 
                  backgroundColor: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: '4px',
                  textAlign: 'center'
                }}>
                  <Text style={{ fontSize: '12px', color: '#52c41a', fontWeight: 500 }}>
                    {((100 * (variant.mrp - variant.price)) / variant.mrp).toFixed(0)}% Discount
                  </Text>
                </div>
              )}
            </div>
          </div>

          {/* Price Section */}
          {storeId && (
            <div style={{ marginBottom: '16px' }}>
              {variant.price === "0.00" ? (
                <Text strong style={{ fontSize: '28px', color: '#000' }}>
                  ₹ {variant.mrp}
                </Text>
              ) : (
                <div>
                  <Text strong style={{ fontSize: '28px', color: '#000' }}>
                    ₹ {variant.price}
                  </Text>
                  {variant.mrp && variant.mrp !== variant.price && (
                    <>
                      <Text
                        style={{
                          textDecorationLine: "line-through",
                          fontSize: '16px',
                          marginLeft: '12px',
                          color: '#999'
                        }}
                      >
                        ₹ {variant.mrp}
                      </Text>
                      <Badge
                        count={`${((100 * (variant.mrp - variant.price)) / variant.mrp).toFixed(0)}% Off`}
                        style={{ 
                          backgroundColor: "#52c41a", 
                          marginLeft: 12,
                          borderRadius: '4px'
                        }}
                      />
                    </>
                  )}
                </div>
              )}
              
              {/* Quantity */}
              <div style={{ marginTop: '16px' }}>
                <Text style={{ fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                  Quantity
                </Text>
                <Text style={{ color: "#666", fontSize: '14px' }}>
                  {variant.variant_stock} packets
                </Text>
              </div>
            </div>
          )}

          {/* Variants */}
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontSize: '16px', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
              Variants
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {details.variants &&
                details.variants.map((varient, index) => {
                  const isSelected = varient.variant_name === variant.variant_name;
                  return (
                    <span
                      key={index}
                      onClick={() => (
                        setVariant(details.vars[index]),
                        setDisplay(
                          details.vars[index].images &&
                            details.vars[index].images.length > 0 &&
                            details.vars[index].images[0].url
                        )
                      )}
                      style={{
                        fontSize: '14px',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer', // Make sure this is set
                        backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                        color: isSelected ? '#1890ff' : 'var(--text-color, #666)',
                        border: isSelected 
                          ? '2px solid #1890ff' 
                          : '1px solid var(--border-color, #d9d9d9)',
                        transition: 'all 0.3s ease',
                        display: 'inline-block',
                        userSelect: 'none' // Prevent text selection
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.style.backgroundColor = 'var(--hover-bg, rgba(0,0,0,0.04))';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {varient.variant_name}
                    </span>
                  );
                })}
            </div>
          </div>

          {/* Category */}
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontSize: '16px', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
              {details.sub_category_group_name
                ? "Group"
                : details.sub_category_name
                  ? "Sub Category"
                  : "Category"}
            </Text>
            <span style={{
              fontSize: '14px',
              padding: '6px 12px',
              borderRadius: '6px',
              backgroundColor: 'var(--component-background, #fafafa)',
              border: '1px solid var(--border-color, #d9d9d9)',
              color: 'var(--text-color-secondary, #666)',
              display: 'inline-block'
            }}>
              {details.sub_category_group_name
                ? details.sub_category_group_name
                : details.sub_category_name
                ? details.sub_category_name
                : details.category
                ? details.category
                : "NA"}
            </span>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontSize: '16px', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
              Description
            </Text>
            <Text style={{ fontSize: '14px', color: '#666', lineHeight: 1.5 }}>
              {details.description}
            </Text>
          </div>

          {/* Extra Variables */}
          {details.meta && details.meta.extra_field && (
            <div style={{ marginBottom: '16px' }}>
              <Popover
                placement="topLeft"
                content={
                  <div style={{ maxWidth: '300px' }}>
                    {details.meta.extra_field.map((extra, index) => (
                      <div key={index} style={{ marginBottom: '8px' }}>
                        <Text strong style={{ fontSize: '12px', color: '#666' }}>
                          {extra.key}:
                        </Text>
                        <Text style={{ marginLeft: '8px', textTransform: "capitalize" }}>
                          {extra.value}
                        </Text>
                      </div>
                    ))}
                  </div>
                }
                title="View Extra Variables"
                trigger="click"
                visible={popover}
                onVisibleChange={() => setPopover(!popover)}
              >
                <Button style={{
                  borderColor: '#1890ff',
                  color: '#1890ff',
                  borderRadius: '6px',
                  height: '36px'
                }}>
                  <EyeOutlined /> View Extra Variables
                </Button>
              </Popover>
            </div>
          )}
        </div>
      </Sidebar>
    </>
  );
}
